/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Intent:     owner/staff setting when an agenda takes appointments — dense workbench
 * Hierarchy:  week day list leads; bulk tools outline; Guardar stays in parent (filled)
 * Palette:    agenda tokens (teal / cool gray-green) — no mint wash, no purple SaaS
 * Depth:      borders + hairlines only
 * Surfaces:   day strip surface2 when open; quieter when closed; time pickers surface
 * Typography: Plex Sans labels; Plex Mono times (tabular)
 * Spacing:    8px base, tight rows
 */
import React from 'react';
import {
  StyleSheet,
  Switch,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { getShellMainPanelWidth } from '../../shared/layout/useContentLayout';
import { agenda, agendaFonts } from '../domain/agendaTheme';
import {
  RESOURCE_DAY_LABELS,
  type ResourceHourDay,
  type ResourceHourShift,
} from '../domain/types';
import { HmTimeField } from './HmTimeField';

type Props = {
  days: ResourceHourDay[];
  /** Panel-aware wide layout from parent (avoids false-wide with web sidebar). */
  isWide?: boolean;
  disabled?: boolean;
  /** Hide the top explanatory caption when the parent provides a section title. */
  hideCaption?: boolean;
  /** Custom label for the copy-from-store action. */
  copyFromStoreLabel?: string;
  /** Show the apply-weekdays template action. */
  showApplyWeekdays?: boolean;
  onDayChange: (dayOfWeek: number, patch: Partial<ResourceHourDay>) => void;
  onAddShift: (dayOfWeek: number) => void;
  onRemoveShift: (dayOfWeek: number, index: number) => void;
  onShiftChange: (
    dayOfWeek: number,
    index: number,
    field: keyof ResourceHourShift,
    value: string,
  ) => void;
  onCopyFromStore: () => void;
  onApplyWeekdays: () => void;
};

export function ResourceWeekHoursEditor({
  days,
  isWide: isWideProp,
  disabled = false,
  hideCaption = false,
  copyFromStoreLabel = 'Copiar de la tienda',
  showApplyWeekdays = true,
  onDayChange,
  onAddShift,
  onRemoveShift,
  onShiftChange,
  onCopyFromStore,
  onApplyWeekdays,
}: Props) {
  const { width } = useWindowDimensions();
  const isWide =
    isWideProp ?? getShellMainPanelWidth(width) >= CONTENT_WIDE_BREAKPOINT;

  return (
    <View style={styles.wrap}>
      {!hideCaption ? (
        <Text style={styles.caption}>
          Cuándo atiende. El del local está en Tienda online.
        </Text>
      ) : null}

      <View style={styles.actions}>
        <TouchableOpacity
          style={[styles.outlineBtn, disabled && styles.disabled]}
          onPress={onCopyFromStore}
          disabled={disabled}
          accessibilityRole="button"
        >
          <Text style={styles.outlineBtnText}>{copyFromStoreLabel}</Text>
        </TouchableOpacity>
        {showApplyWeekdays ? (
          <TouchableOpacity
            style={[styles.outlineBtn, disabled && styles.disabled]}
            onPress={onApplyWeekdays}
            disabled={disabled}
            accessibilityRole="button"
          >
            <Text style={styles.outlineBtnText}>Aplicar Lun–Vie</Text>
          </TouchableOpacity>
        ) : null}
      </View>

      <View style={styles.days}>
        {days.map((day, index) => {
          const label = RESOURCE_DAY_LABELS[day.dayOfWeek];
          const isLast = index === days.length - 1;

          return (
            <View
              key={day.dayOfWeek}
              style={[
                styles.dayRow,
                isWide && styles.dayRowWide,
                !day.isOpen && styles.dayRowClosed,
                !isLast && styles.dayRowDivider,
              ]}
            >
              <View style={[styles.dayHead, isWide && styles.dayHeadWide]}>
                <Text style={[styles.dayLabel, !day.isOpen && styles.dayLabelClosed]}>
                  {label}
                </Text>
                <Switch
                  value={day.isOpen}
                  onValueChange={(isOpen) => {
                    if (isOpen && day.shifts.length === 0) {
                      onDayChange(day.dayOfWeek, {
                        isOpen: true,
                        shifts: [{ startTime: '09:00', endTime: '18:00' }],
                      });
                    } else {
                      onDayChange(day.dayOfWeek, { isOpen });
                    }
                  }}
                  disabled={disabled}
                  trackColor={{ false: agenda.border, true: agenda.brand }}
                  thumbColor={agenda.surface}
                  accessibilityLabel={`${RESOURCE_DAY_LABELS[day.dayOfWeek]} ${day.isOpen ? 'abierto' : 'cerrado'}`}
                />
              </View>

              {day.isOpen ? (
                <View style={[styles.shifts, isWide && styles.shiftsWide]}>
                  {day.shifts.length === 0 ? (
                    <Text style={styles.emptyShifts}>Sin horarios. Agregá al menos uno.</Text>
                  ) : null}
                  {day.shifts.map((shift, si) => (
                    <View key={si} style={[styles.hoursTimes, isWide && styles.hoursTimesWide]}>
                      <HmTimeField
                        value={shift.startTime}
                        onChange={(v) =>
                          onShiftChange(day.dayOfWeek, si, 'startTime', v)
                        }
                        disabled={disabled}
                        comfortable={isWide}
                        style={isWide ? styles.timeFieldWide : undefined}
                        accessibilityLabel={`${RESOURCE_DAY_LABELS[day.dayOfWeek]} desde`}
                      />
                      <Text style={styles.toLabel}>a</Text>
                      <HmTimeField
                        value={shift.endTime}
                        onChange={(v) =>
                          onShiftChange(day.dayOfWeek, si, 'endTime', v)
                        }
                        disabled={disabled}
                        comfortable={isWide}
                        style={isWide ? styles.timeFieldWide : undefined}
                        accessibilityLabel={`${RESOURCE_DAY_LABELS[day.dayOfWeek]} hasta`}
                      />
                      {!disabled && day.shifts.length > 1 ? (
                        <TouchableOpacity
                          onPress={() => onRemoveShift(day.dayOfWeek, si)}
                          hitSlop={10}
                          accessibilityRole="button"
                          accessibilityLabel="Quitar horario"
                          style={styles.removeHit}
                        >
                          <Text style={styles.shiftRemove}>×</Text>
                        </TouchableOpacity>
                      ) : (
                        <View style={styles.removeHitPlaceholder} />
                      )}
                    </View>
                  ))}
                  {!disabled && day.shifts.length < 3 ? (
                    <TouchableOpacity
                      style={[styles.addShiftBtn, isWide && styles.addShiftBtnWide]}
                      onPress={() => onAddShift(day.dayOfWeek)}
                      accessibilityRole="button"
                      accessibilityLabel="Agregar horario"
                    >
                      <Ionicons name="add" size={14} color={agenda.brand} />
                      <Text style={styles.addShiftText}>Agregar horario</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              ) : null}
            </View>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { gap: 12, marginTop: 4 },
  caption: {
    fontSize: 12,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
    lineHeight: 17,
  },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  outlineBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: agenda.radius,
    backgroundColor: agenda.surface,
    borderWidth: 1,
    borderColor: agenda.border,
    minHeight: 36,
    justifyContent: 'center',
  },
  outlineBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
  },
  disabled: { opacity: 0.5 },
  days: {
    borderWidth: 1,
    borderColor: agenda.border,
    borderRadius: agenda.radius,
    backgroundColor: agenda.surface,
    overflow: 'hidden',
  },
  dayRow: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 10,
    backgroundColor: agenda.surface2,
  },
  dayRowWide: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    paddingVertical: 10,
  },
  dayRowClosed: {
    backgroundColor: agenda.surface,
    paddingVertical: 8,
  },
  dayRowDivider: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: agenda.border,
  },
  dayHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    minHeight: 28,
  },
  dayHeadWide: {
    width: 104,
    flexShrink: 0,
    paddingTop: 2,
  },
  dayLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: agenda.ink,
    fontFamily: agendaFonts.sans,
    flex: 1,
  },
  dayLabelClosed: {
    color: agenda.ink2,
    fontWeight: '600',
  },
  shifts: { gap: 10 },
  shiftsWide: {
    flex: 1,
    minWidth: 0,
    paddingTop: 0,
  },
  emptyShifts: {
    fontSize: 12,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
    marginBottom: 2,
  },
  hoursTimes: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 2,
    flexWrap: 'wrap',
  },
  hoursTimesWide: {
    width: '100%',
    minWidth: 0,
    flexWrap: 'nowrap',
  },
  timeFieldWide: {
    flex: 1,
    flexBasis: 0,
    minWidth: 96,
  },
  toLabel: {
    fontSize: 12,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
    width: 16,
    textAlign: 'center',
  },
  removeHit: {
    width: 36,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeHitPlaceholder: {
    width: 36,
  },
  shiftRemove: {
    fontSize: 20,
    lineHeight: 22,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  addShiftBtn: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: agenda.radius,
    borderWidth: 1,
    borderColor: agenda.border,
    backgroundColor: agenda.surface,
    minHeight: 38,
    justifyContent: 'center',
    marginTop: 2,
  },
  addShiftBtnWide: {
    paddingVertical: 4,
  },
  addShiftText: {
    fontSize: 12,
    color: agenda.brand,
    fontWeight: '600',
    fontFamily: agendaFonts.sans,
  },
});
